import {
  ItemSetQuestionPropertiesLocationType,
  OrderType,
  QuestionTagCategory,
  useGetItemSetQuestionPropertiesByStatusAndLocationQuery,
  useGetTagsByCategoryQuery,
  useSearchLearningObjectiveStatementsQuery,
  type ItemSetQuestionPropertiesStatus,
} from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { Loading, Box } from '@shared/components';
import { useMemo } from 'react';

import { useQBankStructureDashBoardSearchParams } from '../../hooks';
import { QBankStructureDashBoardNavigationTabs } from '../qbank-structure-dashboard-navigation-tabs';
import { QBankStructureLosItemSetQuestionDashboardTable } from '../qbank-structure-los-item-set-question-dashboard-table';

export default function QBankStructureLosItemSetQuestionDashBoard() {
  const { qBankStructureInformation } = useQBankStructure();
  const [{ status }] = useQBankStructureDashBoardSearchParams();
  const { data, loading: isItemSetQuestionPropertiesLoading } =
    useGetItemSetQuestionPropertiesByStatusAndLocationQuery({
      variables: {
        input: {
          status: status as ItemSetQuestionPropertiesStatus,
          qBankStructureId: qBankStructureInformation.id,
          locationType: ItemSetQuestionPropertiesLocationType.LearningObjectiveStatement,
        },
      },
    });

  const { data: getDifficultyTagsResult, loading: isDifficultyTagLoading } =
    useGetTagsByCategoryQuery({
      variables: { input: { category: QuestionTagCategory.Difficulty } },
    });

  const { data: getCalculationTagsResult, loading: isCalculationTagLoading } =
    useGetTagsByCategoryQuery({
      variables: { input: { category: QuestionTagCategory.Calculation } },
    });

  const { data: searchLOSResult, loading: isLOSLoading } =
    useSearchLearningObjectiveStatementsQuery({
      skip: false,
      variables: {
        order: {
          field: 'createdAt',
          type: OrderType.Asc,
        },
        paging: {
          currentPage: 0,
          pageSize: 9999, // NOTE: this is a workaround to get all LOS by estimation
        },
        search: {
          qBankStructureId: qBankStructureInformation.id,
        },
      },
    });

  const itemSetQuestionPropertiesData = useMemo(
    () => data?.qctGetItemSetQuestionPropertiesByStatusAndLocation ?? [],
    [data],
  );

  const losData = useMemo(
    () => searchLOSResult?.qbsSearchLearningObjectiveStatements.learningObjectiveStatements ?? [],
    [searchLOSResult],
  );

  const difficultyTagsData = useMemo(
    () => getDifficultyTagsResult?.qbsGetTagsByCategory ?? [],
    [getDifficultyTagsResult],
  );

  const calculationTagsData = useMemo(
    () => getCalculationTagsResult?.qbsGetTagsByCategory ?? [],
    [getCalculationTagsResult],
  );

  const isLoading =
    isItemSetQuestionPropertiesLoading ||
    isLOSLoading ||
    isCalculationTagLoading ||
    isDifficultyTagLoading;

  return (
    <Box
      sx={(theme) => ({
        width: '100%',
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: theme.shape.rounded(2),
        overflow: 'hidden',
      })}
    >
      <QBankStructureDashBoardNavigationTabs
        questionStatuses={['PUBLISHED', 'UNPUBLISHED', 'DISABLED']}
      />
      {isLoading ? (
        <Box sx={(theme) => ({ p: theme.spacing(6) })}>
          <Loading />
        </Box>
      ) : (
        <QBankStructureLosItemSetQuestionDashboardTable
          data={itemSetQuestionPropertiesData}
          difficultyTags={difficultyTagsData}
          calculationTags={calculationTagsData}
          los={losData}
        />
      )}
    </Box>
  );
}
